import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import api from 'state/axios';
import { 
  useTheme, useMediaQuery, Box, 
  CircularProgress, Typography, Tooltip, IconButton, Chip 
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import { hasPermission } from 'state/checkPermission';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import DataGridComponent from 'components/DataGridComponent';
import CardList from 'components/CardList';


const ChecklistItems = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user); 
  const theme = useTheme();  
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const canCreate = hasPermission(user?.permissions, "ChecklistItems", "create", false);
  const canEdit = hasPermission(user?.permissions, "ChecklistItems", "update", false);
  const canDelete = hasPermission(user?.permissions, "ChecklistItems", "delete", false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]); 
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [idToDelete, setIdToDelete] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });

  
  const fetchItems = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);  

      const res = await api.get('/checklistitems');        
      setItems(res.data);
    } catch (err) {
      setError(err);
      toast.error("Failed to fetch checklist items!");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]); 

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      width: 60,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    { 
      field: "code", 
      headerName: "Code", 
      flex: isMobile ? 0 : 1,
      minWidth: 100,
      // renderCell: (params) => <strong>{params.value}</strong>
    },
    {
      field: "description",
      headerName: "Description",
      flex: isMobile ? 0 : 3,
      minWidth: 200,
    },
    { 
      field: "severity", 
      headerName: "Severity", 
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const colors = { critical: 'error', warning: 'warning', info: 'info' };
        return (
          <Chip 
            label={params.value?.toUpperCase()} 
            color={colors[params.value] || 'default'} 
            size="small" 
            variant="outlined"
          />
        );
      }
    },
    { 
      field: "sortOrder", 
      headerName: "SortOrder", 
      type: 'number',
      width: 80,
      align: 'right',
      headerAlign: 'center',
    },
    {
      field: "isActive",
      headerName: "IsActive",
      width: 80,
    },
  ], [isMobile]);

  const actionColumn = [
    {
      field: "action",
      headerName: "Action",
      sortable: false,
      filterable: false,
      width: 90,      
      renderCell: (params) => (
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center"
          gap={1}
          sx={{ width: '100%', height: '100%' }}
        >        
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => {
                if (!canEdit) return;
                navigate(`/checklistitems/${params.row._id}`);
              }}
              sx={{ 
                color: theme.palette.primary.dark, 
                '&:hover': { color: theme.palette.green.deep } 
              }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => {
                if (!canDelete) return;
                setIdToDelete(params.row._id);
                setDeleteType('single');
                setConfirmDialogOpen(true);
              }}
              sx={{ 
                color: theme.palette.primary.dark, 
                '&:hover': { color: theme.palette.error.main } 
              }}
            >
              <DeleteForeverIcon />
            </IconButton>
          </Tooltip>          
        </Box>
      ),
    },
  ];

  const handleBulkDelete = () => {
    const selectedIds = getSelectedIds(rowSelectionModel, items);    
    if (selectedIds.length === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }
    
    setDeleteType('bulk');
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try {
      if (deleteType === 'single') {
        await api.delete(`/checklistitems/delete/${idToDelete}`);
        setItems(prev => prev.filter(item => item._id !== idToDelete));
        toast.success('Item deleted successfully');
      } else {
        const selectedIds = getSelectedIds(rowSelectionModel, items);
        await api.patch('/checklistitems/soft-delete', { ids: selectedIds });
        setItems(prev => prev.filter(item => !selectedIds.includes(item._id)));
        toast.success('Selected items deleted');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
    } finally {
      setConfirmDialogOpen(false);
      setRowSelectionModel([]);
    }
  }, [
    deleteType, 
    idToDelete, 
    rowSelectionModel, 
    items
  ]);


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Checklist Items" 
          subtitle={loading ? 'Loading...' : `List of ${items.length} items`}
        />
        {!loading && (
          <Box display="flex" gap={1}>
            {!isMobile && (<Tooltip title="Bulk soft delete">
              <span>
                <StyledButton 
                  disabled={!isAnyRowSelected(rowSelectionModel, items)}
                  onClick={() => {
                    handleBulkDelete();
                  }}
                >
                  <DeleteIcon />
                </StyledButton>
              </span>
            </Tooltip>)}

            <Tooltip title="Create">
              <span>
                <StyledButton 
                  onClick={() => {
                    if (!canCreate) return;
                    navigate("/checklistitems/new");
                  }}
                >
                  <AddCircleOutlineIcon />
                </StyledButton>
              </span>              
            </Tooltip>
          </Box>
        )}
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}><CircularProgress /></FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message}</Typography>
        </FlexCenter>
      ) : (        
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          gap={2}
          sx={{
            "& > div": { gridColumn: isDesktop ? undefined : "span 12" },
          }}
        >
          <Box
            height={LAYOUT_HEIGHT}
            sx={{ gridColumn: "span 12" }}
          >
            {isMobile ? (
              <CardList
                rows={items}
                primaryField="code"
                fields={[
                  { label: "Description", key: "description" },
                  { label: "Severity", key: "severity" },
                  { label: "Sort Order", key: "sortOrder" },
                ]}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={(id) => navigate(`/checklistitems/${id}`)}
                onDelete={(id) => {
                  setIdToDelete(id);
                  setDeleteType('single');
                  setConfirmDialogOpen(true);
                }}
              />
            ) : (
            <DataGridComponent
              rows={items}
              columns={columns.concat(actionColumn)}
              columnVisibilityModel={columnVisibilityModel}
              setColumnVisibilityModel={setColumnVisibilityModel}
              loading={loading}
              props={{
                checkboxSelection: true,
                disableRowSelectionOnClick: true,
                onRowSelectionModelChange: setRowSelectionModel,
                rowSelectionModel,
                getRowClassName: (params) => !params.row.isActive ? 'isActive-false' : '',
                initialState: { pagination: { paginationModel: { pageSize: 100 } } },
                sx: { "& .isActive-false": { bgcolor: theme.palette.action.hover } },
              }}
            />
            )}
          </Box>
        </Box>
      )}

      <DeleteConfirm
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeDelete}
        deleteType={deleteType}
      />
    </Box>
  );
};

export default ChecklistItems;